import React, { Suspense } from 'react';
import { Box, CircularProgress, styled } from '@mui/material';
import { Navigate, Route, Routes } from 'react-router-dom';
import { gql, useQuery } from 'urql';
import { NotificationAutoAck, NotificationsProvider } from '../../contexts/notifications';
import { FetchCurrentUserQuery, FetchCurrentUserQueryVariables } from '../../generated/hooks';
import { APP_HEADER_HEIGHT_SM, APP_HEADER_HEIGHT_XS } from '../../utils';
import EmailVerification from '../EmailVerification';
import AdminManageTeams from './AdminManageTeams';
import AdminWeeklyPayouts from './AdminWeeklyPayouts';
import AdminWeeklyRankings from './AdminWeeklyRankings';
import AppHeader from './AppHeader';
import ChangePasswordForm from './ChangePasswordForm';
import Home from './Home';
import LeagueDetail from './LeagueDetail';
import NotificationPreferences from './NotificationPreferences';
import Notifications from './Notifications';

// LogosList statically imports every team logo directly (it's a QA reference page for eyeballing each
// team's colors, not something on the golden path) -- if it stayed a normal import, those 138 static
// imports would force every logo into this main chunk regardless of TeamLogoComponent's own lazy-loading
// (see components/logos/index.tsx), since a module needed synchronously by an initial chunk always ships
// there. Lazy-loading the page itself is what actually lets the per-team logo chunks split out.
const LogosList = React.lazy(() => import(/* webpackChunkName: "page-logos-reference" */ './LogosList'));

export const fetchCurrentUser = gql`
  query fetchCurrentUser {
    currentUser {
      id
      fullName
      isEmailVerified
      isSuperuser
      dateJoined
    }
  }
`;

const AppContent = styled('div')(({ theme }) => ({
  marginTop: APP_HEADER_HEIGHT_XS,
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    marginTop: APP_HEADER_HEIGHT_SM,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

function App() {
  const [{ data }] = useQuery<FetchCurrentUserQuery, FetchCurrentUserQueryVariables>({
    query: fetchCurrentUser,
  });
  if (!data) {
    return (
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!data.currentUser.isEmailVerified) {
    return (
      <>
        <AppHeader currentUser={data?.currentUser} />
        <AppContent>
          <Routes>
            <Route path="/verify-email" element={<EmailVerification />} />
            <Route path="*" element={<Navigate to="/verify-email" />} />
          </Routes>
        </AppContent>
      </>
    );
  }

  return (
    <NotificationsProvider>
      <NotificationAutoAck />
      <AppHeader currentUser={data?.currentUser} />
      <AppContent>
        <Routes>
          <Route path="/change-password" element={<ChangePasswordForm />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/notifications/preferences" element={<NotificationPreferences />} />
          {data.currentUser.isSuperuser && <Route path="/admin/weekly-payouts" element={<AdminWeeklyPayouts />} />}
          {data.currentUser.isSuperuser && (
            <Route path="/admin/weekly-rankings" element={<AdminWeeklyRankings />} />
          )}
          {data.currentUser.isSuperuser && (
            <Route path="/admin/manage-teams" element={<AdminManageTeams />} />
          )}
          <Route path="/league/:leagueId/*" element={<LeagueDetail />} />
          <Route path="/" element={<Home currentUser={data.currentUser} />} />
          <Route
            path="/logos"
            element={
              <Suspense
                fallback={
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <CircularProgress />
                  </Box>
                }
              >
                <LogosList />
              </Suspense>
            }
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </AppContent>
    </NotificationsProvider>
  );
}

export default App;
